import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, ButtonBase, CircularProgress, Tooltip, Typography } from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HistoryIcon from '@mui/icons-material/History';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LinkIcon from '@mui/icons-material/Link';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getConversationCustomer, relinkConversationCustomer } from '../../api/communicationApi';
import DuplicateReviewDialog from './DuplicateReviewDialog';
import T, { useAutoTranslate } from '../common/T';

const STATUS = {
  known: { label: 'Known customer', color: '#15803d', bg: '#ecfdf3', border: '#bbf7d0', Icon: VerifiedUserOutlinedIcon },
  chat_only: { label: 'Not in Actor Management yet', color: '#b45309', bg: '#fffbeb', border: '#fde68a', Icon: PersonAddAlt1OutlinedIcon },
  new: { label: 'New customer', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', Icon: PersonAddAlt1OutlinedIcon },
  unavailable: { label: 'Customer check unavailable', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', Icon: WarningAmberIcon },
};

const Fact = ({ icon, children, tone = '#334155', title }) => (
  <Tooltip title={title || ''} disableHoverListener={!title} arrow>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: tone, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {icon}
      {children}
    </Box>
  </Tooltip>
);

Fact.propTypes = { icon: PropTypes.node, children: PropTypes.node, tone: PropTypes.string, title: PropTypes.string };

/**
 * One line above the chat: who the customer is, their open and past repairs,
 * other chats, and a Reconnect action when the chat lost its contact.
 */
const CustomerRecognitionBar = ({ conversationId, canEdit, canMerge = false, onOpenConversation, onOpenRepair, onLinked, refreshKey }) => {
  const at = useAutoTranslate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [notice, setNotice] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);

  const load = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    try {
      setData(await getConversationCustomer(conversationId));
    } catch (err) {
      setData({ status: 'unavailable' });
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    setData(null);
    setNotice('');
    load();
  }, [load, refreshKey]);

  const relink = async (create) => {
    setLinking(true);
    setNotice('');
    try {
      const result = await relinkConversationCustomer(conversationId, { create });
      if (result.status === 'linked') {
        setNotice(result.created ? 'New contact created and connected.' : `Connected to ${result.contact?.name || 'the contact'}.`);
        await load();
        if (typeof onLinked === 'function') onLinked(result.contact);
      } else if (result.status === 'no_match') {
        setNotice('No contact owns this phone number or e-mail yet.');
      } else if (result.status === 'no_contact_details') {
        setNotice('This chat has no phone number or e-mail to match.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setNotice(typeof detail === 'string' ? detail : 'The chat could not be connected. Try again.');
    } finally {
      setLinking(false);
    }
  };

  if (!conversationId || data?.status === 'group') return null;
  if (!data) {
    return loading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderBottom: '1px solid #eef2f7', bgcolor: '#fbfdfb' }}>
        <CircularProgress size={13} sx={{ color: '#32cd32' }} />
        <Typography sx={{ fontSize: 12.5, color: '#64748b' }}><T>Checking customer…</T></Typography>
      </Box>
    ) : null;
  }

  const style = STATUS[data.status] || STATUS.unavailable;
  const { Icon } = style;
  const contact = data.contact;
  const openRepairs = data.repairs?.open || [];
  const pastCount = data.repairs?.past_count || 0;
  const otherChats = (data.conversations || []).filter((conv) => conv.id !== conversationId);
  const openOtherChats = otherChats.filter((conv) => conv.is_open);
  const needsAction = data.needs_relink || data.status === 'chat_only';

  return (
    <Box
      role="region"
      aria-label={at("Customer recognition")}
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1, sm: 1.75 },
        px: 2, py: 0.85, borderBottom: '1px solid', borderColor: needsAction ? style.border : '#eef2f7',
        bgcolor: needsAction ? style.bg : '#fbfdfb', flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.35, borderRadius: 999, bgcolor: style.bg, border: `1px solid ${style.border}`, color: style.color }}>
        <Icon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap' }}>{style.label}</Typography>
      </Box>

      {contact?.archived && (
        <Fact icon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />} tone="#475569" title={at("The contact is archived. History stays available; restore it from the contact details.")}><T>
          Archived contact
        </T></Fact>
      )}

      {data.status === 'known' && (
        <>
          {openRepairs.length > 0 ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Fact icon={<BuildOutlinedIcon sx={{ fontSize: 15 }} />} tone="#15803d">
                {openRepairs.length === 1 ? 'Open repair' : `${openRepairs.length} open repairs`}
              </Fact>
              {openRepairs.slice(0, 3).map((repair) => (
                <ButtonBase
                  key={repair.repair_id || repair.process_id}
                  onClick={() => onOpenRepair && onOpenRepair(repair)}
                  disabled={!onOpenRepair}
                  sx={{ px: 0.85, py: 0.15, borderRadius: 999, bgcolor: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 800, fontFamily: 'inherit' }}
                >
                  {repair.repair_id || 'Repair'}
                </ButtonBase>
              ))}
            </Box>
          ) : (
            <Fact icon={<BuildOutlinedIcon sx={{ fontSize: 15 }} />} tone="#64748b"><T>No open repair</T></Fact>
          )}
          <Fact
            icon={<HistoryIcon sx={{ fontSize: 15 }} />}
            tone={pastCount ? '#334155' : '#94a3b8'}
            title={pastCount ? (data.repairs.past || []).map((repair) => repair.repair_id).filter(Boolean).join(', ') : ''}
          >
            {pastCount === 0 ? 'No earlier repairs' : pastCount === 1 ? '1 earlier repair' : `${pastCount} earlier repairs`}
          </Fact>
        </>
      )}

      {otherChats.length > 0 && (
        <ButtonBase
          onClick={() => onOpenConversation && onOpenConversation(openOtherChats[0] || otherChats[0])}
          disabled={!onOpenConversation}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#334155', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', borderRadius: 1, px: 0.5 }}
        >
          <ForumOutlinedIcon sx={{ fontSize: 15 }} />
          {otherChats.length === 1 ? '1 other chat' : `${otherChats.length} other chats`}
          {openOtherChats.length > 0 ? ` (${openOtherChats.length} open)` : ''}
        </ButtonBase>
      )}

      {data.possible_duplicates && (
        <ButtonBase
          onClick={() => setReviewOpen(true)}
          disabled={!canMerge}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#b45309', fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit', borderRadius: 1, px: 0.5 }}
        >
          <WarningAmberIcon sx={{ fontSize: 15 }} />
          {`${(data.other_contacts || []).length + 1} contacts share these details`}
          {canMerge ? ' · Review' : ''}
        </ButtonBase>
      )}
      <DuplicateReviewDialog
        open={reviewOpen}
        contacts={[contact, ...(data.other_contacts || [])]}
        onClose={() => setReviewOpen(false)}
        onMerged={(kept) => {
          setNotice(`Merged into ${kept?.name || 'the kept contact'}.`);
          load();
          if (typeof onLinked === 'function') onLinked(kept);
        }}
      />

      <Box sx={{ flex: 1 }} />

      {notice && <Typography sx={{ fontSize: 12.5, color: '#334155', fontWeight: 600 }}><T>{notice}</T></Typography>}

      {canEdit && data.needs_relink && contact && (
        <Button
          size="small"
          onClick={() => relink(false)}
          disabled={linking}
          startIcon={linking ? <CircularProgress size={13} color="inherit" /> : <LinkIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontWeight: 800, color: '#15803d', borderRadius: 2 }}
        ><T>
          Connect to </T>{contact.name || 'contact'}
        </Button>
      )}
      {canEdit && !contact && data.status !== 'unavailable' && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => relink(true)}
          disabled={linking}
          startIcon={linking ? <CircularProgress size={13} color="inherit" /> : <PersonAddAlt1OutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontWeight: 800, color: '#1d4ed8', borderColor: '#bfdbfe', borderRadius: 2, bgcolor: '#fff' }}
        ><T>
          Create contact
        </T></Button>
      )}
    </Box>
  );
};

CustomerRecognitionBar.propTypes = {
  conversationId: PropTypes.string,
  canEdit: PropTypes.bool,
  canMerge: PropTypes.bool,
  onOpenConversation: PropTypes.func,
  onOpenRepair: PropTypes.func,
  onLinked: PropTypes.func,
  refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CustomerRecognitionBar;
